import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { MAIN_SITE_URL, categories } from '@/mocks/categories';

const navItems = [
  { key: 'nav.home', to: '/' },
  { key: 'nav.categories', to: '/#categories' },
  { key: 'nav.guide', to: '/article/usfans-spreadsheet-guide' },
  { key: 'nav.faq', to: '/#faq' },
  { key: 'nav.safety', to: '/#safety' },
];

export default function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isHome = location.pathname === '/';

  return (
    <header className="sticky top-0 z-40 border-b-2 border-background-300 bg-background-50">
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2 text-foreground-950">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-grid-line text-lg"></i>
          </span>
          <span className="font-heading text-lg font-bold tracking-tight">{t('brand')}</span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <Link
              key={item.key}
              to={item.to}
              className="rounded-full px-3 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100 hover:text-foreground-950 whitespace-nowrap"
            >
              {t(item.key)}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="hidden rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex whitespace-nowrap"
          >
            {t('nav.mainWebsite')}
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground-900 lg:hidden"
            aria-label="Menu"
          >
            <i className={open ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
          </button>
        </div>
      </div>

      {open && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100"
              >
                {t(item.key)}
              </Link>
            ))}
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-1 rounded-lg bg-primary-500 px-3 py-2.5 text-center text-sm font-semibold text-background-50"
            >
              {t('nav.mainWebsite')}
            </a>
          </nav>

          <div className="mt-4 border-t border-background-200 pt-4">
            <p className="px-3 text-xs font-bold uppercase tracking-wide text-foreground-500">
              {t('categories.dropdownLabel')}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              {categories.map((c) => (
                <a
                  key={c.id}
                  href={c.href}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-100 px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-primary-100/60 whitespace-nowrap"
                >
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center text-primary-600">
                    <i className={c.icon}></i>
                  </span>
                  <span className="truncate">{t(c.nameKey)}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {isHome && <div className="sr-only">{t('brand')}</div>}
    </header>
  );
}